import type { UserBasic } from '@portal/pkg-api/gen/auth'
import type { Enablement } from '@portal/pkg-api/gen/shared'

export type IlmPermission =
  | 'READ'
  | 'UPDATE'
  | 'CREATE'
  | 'DELETE'
  | 'ADMIN'
  | 'WRITE'
  | 'MANAGE'
  | 'LOCK'
  | 'UNLOCK'
  | 'KEY'
  | 'EXECUTE'
  | 'FEATURE'
  | 'ALL'
  | 'READ_BULK'
  | 'UPDATE_BULK'
  | 'CREATE_BULK'
  | 'DELETE_BULK'
  | 'ADMIN_BULK'
  | 'WRITE_BULK'
  | 'MANAGE_BULK'
  | 'LOCK_BULK'
  | 'UNLOCK_BULK'
  | 'KEY_BULK'
  | 'EXECUTE_BULK'
  | 'ALL_BULK'

interface EnablementPermissions {
  standard: IlmPermission[]
  qualified: string[]
}

interface EnablementMap {
  [target: string]: EnablementPermissions
}

export class Enablements {
  private readonly enablements: EnablementMap

  constructor(enablementArr: Enablement[]) {
    this.enablements = enablementArr.reduce((acc: EnablementMap, enablement) => {
      return {
        ...acc,
        [enablement.target]: {
          standard: [...enablement.permissions.standard] as IlmPermission[],
          qualified: [...enablement.permissions.qualified],
        },
      }
    }, {})
  }

  public hasStandardEnablement(target: string, permission: IlmPermission): boolean {
    return this.enablements[target]?.standard.includes(permission) ?? false
  }

  public hasQualifiedEnablement(
    target: string,
    permission: IlmPermission,
    qualifier: string,
  ): boolean {
    return this.enablements[target]?.qualified.includes(`${permission}.${qualifier}`) ?? false
  }
}

/** Whether the Preferences (administration) area has anything to show this user. */
export function hasAdminViewEnablement(enablements: Enablements, effectiveUser?: UserBasic) {
  return (
    enablements.hasStandardEnablement('Menu', 'UPDATE') ||
    enablements.hasStandardEnablement('Entity', 'ADMIN') ||
    (effectiveUser?.orgId === 1 && enablements.hasStandardEnablement('Organization', 'ADMIN')) ||
    enablements.hasStandardEnablement('Dashboard', 'UPDATE')
  )
}
